import { DestroyRef, Injectable } from '@angular/core';
import { AccessOptions } from '@shared/models/accessOptions.model';
import { AllFormation } from '@shared/models/allFormation.model';
import { Formation } from '@shared/models/formation.model';
import { FormationsGroups } from '@shared/models/formationsGroups.model';
import { HospitalNames, HospitalTypes } from '@shared/models/hospitalTypes.model';
import { Personas } from '@shared/models/personas.model';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { uniqueId } from 'lodash';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { StorageService } from 'src/app/core/services/storage.service';
import { UserDal } from '../dal/user.dal';
import { Hospital, HospitalWithDisplayName } from '../models/hospital.model';
import { Profile } from '../models/profile.model';
import { User } from '../models/user.model';
import { ErrorService } from './error.service';
import { ParamedicalService } from './paramedical.service';
import { addDisplayNameToHospitals } from '@shared/utils/cross-functions';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private instanceId: string;

  public changedHospitalsSelectedSubject: Subject<void> = new Subject<void>();

  public soloHospitalModeSubject: Subject<any> = new Subject<boolean>();

  public sideNav: Subject<void> = new Subject<void>();

  public jobDescriptionToggle: Subject<boolean> = new Subject<boolean>();

  public rebuildMenuSubject: Subject<void> = new Subject<void>();

  private selectedHeaderBehaviorSubject: Subject<string> = new BehaviorSubject<string>('Bloc');

  private allFormations: AllFormation[] | undefined;

  private allFormationsGroups: FormationsGroups[] | undefined;

  private allFormationsGroupsPromise: Promise<FormationsGroups[]> | null = null;

  private allFormationsPromise: Promise<AllFormation[]> | null = null;

  public usersHospitals: HospitalWithDisplayName[] | undefined;

  constructor(
    private userDal: UserDal,
    private storageService: StorageService,
    private paramedicalService: ParamedicalService,
    private errorService: ErrorService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.initializeUsersHospitals();
  }

  initializeUsersHospitals() {
    this.usersHospitals = this.getCurrentUserHospitals();
  }

  resolveHospitalName(hospitalId: string) {
    const hospital = this.usersHospitals?.find((hospital) => hospital._id === hospitalId);
    return hospital?.displayName ?? '';
  }

  getAllUsers(options?: any): Observable<User[]> {
    return this.userDal.getAllUsers(options);
  }

  addUser(user: User): Observable<any> {
    return this.userDal.addUser(user);
  }

  addUsers(users: User[]): Observable<any> {
    return this.userDal.addUsers(users);
  }

  modifyUser(user: User): Observable<User> {
    return this.userDal.modifyUser(user);
  }

  modifyUserMail(userId: string, email: string): Observable<User> {
    return this.userDal.modifyUserMail(userId, email);
  }

  hardDeleteUser(user: User): Observable<User> {
    return this.userDal.hardDeleteUser(user);
  }

  archiveUser(idUser: string): Observable<User> {
    return this.userDal.archiveUser(idUser);
  }

  getUser(idUser: string): Observable<any> {
    return this.userDal.getUser(idUser);
  }

  getUserByProfileId(profileId: string): Observable<any> {
    return this.userDal.getUserByProfileId(profileId);
  }

  getUsersCounter(): Observable<number> {
    return this.userDal.getUsersCounter();
  }

  getPersonas(): Observable<Personas[]> {
    return this.userDal.getPersonas();
  }

  checkAccessOptions(): Observable<any> {
    return this.userDal.checkAccessOptions((this.getCurrentUser().persona as Personas).uniqueHash!);
  }

  verifyOtp(code: string, twoFactorCookie: string): Observable<any> {
    return this.userDal.verifyOtp(code, twoFactorCookie);
  }

  resendOtp(twoFactorCookie: string): Observable<any> {
    return this.userDal.resendOtp(twoFactorCookie);
  }

  validatePassword(password: string): Observable<{ valid: boolean }> {
    return this.userDal.validatePassword(password);
  }

  getCurrentUserHospitals() {
    const user = this.storageService.getUser();
    let hospitals = user?.profile?.hospitals;

    hospitals = hospitals?.filter((hospital: Hospital) => {
      return !this.storageService.getUser()?.profile?.desactivatedAt?.includes(String(hospital._id));
    });

    addDisplayNameToHospitals(hospitals ?? []);
    return hospitals;
  }

  getUserPrincipalHospitalId() {
    // supprimer la condition après 30 jours (03/25)
    if (this.storageService.getUser()?.profile?.principalHospitalId) {
      return this.storageService.getUser()?.profile?.principalHospitalId;
    }
    return this.storageService.getUser()?.profile?.hospitals?.[0]?._id;
  }

  getSelectedHospitals() {
    return this.storageService.getData('selectedHospitalsId');
  }

  saveSelectedHospitals(hospitalsId: Array<string>) {
    this.storageService.saveData('selectedHospitalsId', hospitalsId);
  }

  isCurrentUserHasMultipleHospitals(): boolean {
    const hospitalsNumber = this.storageService.getUser()?.profile?.hospitals?.length;
    const desactivatedHospitalsNumber = this.storageService.getUser()?.profile?.desactivatedAt?.length
      ? this.storageService.getUser()?.profile?.desactivatedAt?.length
      : 0;
    const activeUserHospitalsNumber = (hospitalsNumber ?? 0) - (desactivatedHospitalsNumber ?? 0);

    return activeUserHospitalsNumber > 1;
  }

  isProfileHasMultipleHospitals(profile: Profile | undefined): boolean {
    if (!profile) {
      return false;
    }
    const hospitalsNumber = profile.hospitals?.length;
    const desactivatedHospitalsNumber = profile.desactivatedAt?.length;
    const activeProfileHospitalsNumber = (hospitalsNumber ?? 0) - (desactivatedHospitalsNumber ?? 0);

    return activeProfileHospitalsNumber > 1;
  }

  isCurrentUserHasMultipleHospitalsSelected(): boolean {
    const selectedHospitals = this.storageService.getData('selectedHospitalsId');
    return selectedHospitals?.length > 1;
  }

  getCurrentUser(): User {
    return this.storageService.getUser();
  }

  getAccessOptionsOfPage(pageUrl: string, checkWith?: { keyName: string; checkWith: any }): AccessOptions | undefined {
    const user = this.getCurrentUser();
    if (!user) {
      return undefined;
    }

    const accessOptions = (user.persona as Personas)?.accessOptions as AccessOptions[];

    const filteredPageUrl = pageUrl.split('?')[0].split('#')[0];

    if (!accessOptions) {
      return undefined;
    }

    // First, filter options based on checkWith condition if provided
    const filteredOptions = checkWith
      ? accessOptions.filter((option: AccessOptions) => {
          return (
            option &&
            checkWith.keyName in option &&
            String(option[checkWith.keyName as keyof AccessOptions]) === String(checkWith.checkWith)
          );
        })
      : accessOptions;

    // Look for exact matches in the filtered options
    const exactMatch = filteredOptions.find((option: AccessOptions) => {
      return option && option.pageUrl && !option.dynamicUrl && option.pageUrl === filteredPageUrl;
    });

    if (exactMatch) {
      return exactMatch;
    }

    // Look for dynamic matches in the filtered options
    const dynamicMatches = filteredOptions.filter((option: AccessOptions) => {
      return option && option.pageUrl && option.dynamicUrl && filteredPageUrl.includes(option.pageUrl);
    });

    if (!dynamicMatches.length) {
      return undefined;
    }

    dynamicMatches.sort((a: AccessOptions, b: AccessOptions) => (b.pageUrl?.length ?? 0) - (a.pageUrl?.length ?? 0));

    return dynamicMatches[0];
  }

  getAllFormations(): Promise<AllFormation[]> {
    if (this.allFormations) {
      return Promise.resolve(JSON.parse(JSON.stringify(this.allFormations)));
    }

    if (this.allFormationsPromise) {
      return this.allFormationsPromise;
    }

    this.allFormationsPromise = new Promise(
      (resolve: (formations: AllFormation[]) => void, reject: (error: any) => void) => {
        this.subscriptionManager
          .createRequest(this.paramedicalService.getAllFormations(), 'getAllFormations', this.instanceId)
          .subscribe({
            next: (formations: AllFormation[]) => {
              this.allFormations = formations;
              this.allFormationsPromise = null;
              resolve(formations);
            },
            error: (error: any) => {
              this.allFormationsPromise = null;
              this.errorService.handleError(error);
              reject(error);
            },
          });
      }
    );

    return this.allFormationsPromise;
  }

  getAllFormationsGroups(): Promise<FormationsGroups[]> {
    if (this.allFormationsGroups) {
      return Promise.resolve(JSON.parse(JSON.stringify(this.allFormationsGroups)));
    }

    if (this.allFormationsGroupsPromise) {
      return this.allFormationsGroupsPromise;
    }

    this.allFormationsGroupsPromise = new Promise(
      (resolve: (formationsGroups: FormationsGroups[]) => void, reject: (error: any) => void) => {
        this.subscriptionManager
          .createRequest(this.paramedicalService.getAllFormationsGroups(), 'getAllFormationsGroups', this.instanceId)
          .subscribe({
            next: (formationsGroups: FormationsGroups[]) => {
              this.allFormationsGroups = formationsGroups;
              this.allFormationsGroupsPromise = null;
              resolve(formationsGroups);
            },
            error: (error: any) => {
              this.allFormationsGroupsPromise = null;
              this.errorService.handleError(error);
              reject(error);
            },
          });
      }
    );

    return this.allFormationsGroupsPromise;
  }

  async getAccessOptionsPositionsByPositions(
    positions: string[] | undefined,
    fromHospitals?: boolean
  ): Promise<Formation[]> {
    if (!positions || positions.length === 0) {
      return [];
    }

    const formations: Formation[] = fromHospitals
      ? await firstValueFrom(
          this.subscriptionManager.createRequest(
            this.paramedicalService.getFormations(),
            'getFormations',
            this.instanceId,
            true
          )
        )
      : await this.getAllFormations();

    const formationGroups = await this.getAllFormationsGroups();

    const groupMap = new Map<string, any>();
    for (const group of formationGroups) {
      if (group._id) {
        groupMap.set(group._id.toString(), group);
      }
    }

    if (fromHospitals) {
      const allowedFormationNames = new Set<string>();

      const allFormations = await this.getAllFormations();
      const formationIdToName = new Map<string, string>();
      const formationNameSet = new Set<string>();
      for (const f of allFormations) {
        if (f._id && f.name) {
          formationIdToName.set(f._id.toString(), f.name);
          formationNameSet.add(f.name);
        }
      }

      for (const pos of positions) {
        const group = groupMap.get(pos);
        if (group && Array.isArray(group.formations)) {
          for (const formationId of group.formations) {
            const name = formationIdToName.get(formationId.toString());
            if (name) {
              allowedFormationNames.add(name);
            }
          }
        } else if (formationNameSet.has(pos)) {
          allowedFormationNames.add(pos);
        }
      }

      const filteredHospitalFormations = formations.filter((f: any) => allowedFormationNames.has(f.name));

      return filteredHospitalFormations.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    const groupToFormationIds = new Map<string, string[]>();
    for (const group of formationGroups) {
      if (group._id && Array.isArray(group.formations)) {
        groupToFormationIds.set(
          group._id.toString(),
          group.formations.map((f: any) => f.toString())
        );
      }
    }

    const formationIdMap = new Map<string, Formation>();
    const formationNameMap = new Map<string, Formation>();
    for (const formation of formations) {
      if (formation._id) {
        formationIdMap.set(formation._id.toString(), formation);
      }
      if (formation.name) {
        formationNameMap.set(formation.name, formation);
      }
    }

    const matchedFormations: Formation[] = [];

    for (const pos of positions) {
      if (typeof pos === 'string') {
        const byName = formationNameMap.get(pos);
        if (byName) {
          matchedFormations.push(byName);
          continue;
        }

        const groupFormationIds = groupToFormationIds.get(pos);
        if (groupFormationIds) {
          for (const formationId of groupFormationIds) {
            const formation = formationIdMap.get(formationId);
            if (formation) {
              matchedFormations.push(formation);
            }
          }
        }
      }
    }

    const uniqueFormations = Array.from(
      new Map(matchedFormations.map((f: Formation) => [f._id?.toString(), f])).values()
    );

    return uniqueFormations.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  getSelectedHeader() {
    return this.selectedHeaderBehaviorSubject;
  }

  async arrayOfArrayOfFormationsAndFormationsGroupsToPopulations(
    array: string[][] | undefined
  ): Promise<{ _id: string; name: string; formations: Formation[] }[]> {
    if (!array || array.length === 0) {
      return [];
    }

    const formations: Formation[] = await firstValueFrom(
      this.subscriptionManager.createRequest(
        this.paramedicalService.getFormations(),
        'getFormations',
        this.instanceId,
        true
      )
    );

    const formationGroups = await this.getAllFormationsGroups();

    const groupMap = new Map<string, any>();
    for (const group of formationGroups) {
      if (group._id) {
        groupMap.set(group._id.toString(), group);
      }
    }

    const populations: { _id: string; name: string; formations: Formation[] }[] = [];

    const allFormations = await this.getAllFormations();
    const formationIdToName = new Map<string, string>();
    const formationNameSet = new Set<string>();
    for (const f of allFormations) {
      if (f._id && f.name) {
        formationIdToName.set(f._id.toString(), f.name);
        formationNameSet.add(f.name);
      }
    }

    const formationNameToFormation = new Map<string, Formation>();
    for (const f of formations) {
      if (f._id && f.name) {
        formationNameToFormation.set(f.name, f);
      }
    }

    for (const item of array) {
      const groupFormations: Formation[] = [];
      for (const item2 of item) {
        const group = groupMap.get(item2);
        if (group && Array.isArray(group.formations)) {
          for (const formationId of group.formations) {
            const formationName = formationIdToName.get(formationId.toString());
            if (formationNameToFormation.has(formationName!)) {
              groupFormations.push(formationNameToFormation.get(formationName!)!);
            }
          }
        } else if (formationNameSet.has(item2)) {
          groupFormations.push(formationNameToFormation.get(item2)!);
        }
      }

      populations.push({
        _id: uniqueId('default_population_'),
        name: groupFormations.map((f: Formation) => f.name).join('/'),
        formations: groupFormations,
      });
    }

    return populations;
  }

  async getFormationsOfFormationsGroup(formationsGroup: FormationsGroups, fromHospitals: boolean) {
    if (!formationsGroup || !Array.isArray(formationsGroup.formations)) {
      return [];
    }
    const allFormations = await this.getAllFormations();

    if (!fromHospitals) {
      const formationIdSet = new Set(formationsGroup.formations.map((id: any) => id.toString()));
      return allFormations
        .filter((f: any) => f._id && formationIdSet.has(f._id.toString()))
        .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    const hospitalFormations = await firstValueFrom(
      this.subscriptionManager.createRequest(
        this.paramedicalService.getFormations(),
        'getFormations',
        this.instanceId,
        true
      )
    );

    const formationIdToName = new Map<string, string>();
    for (const f of allFormations) {
      if (f._id && f.name) {
        formationIdToName.set(f._id.toString(), f.name);
      }
    }

    const formationNamesInGroup = new Set<string>();
    for (const id of formationsGroup.formations) {
      const name = formationIdToName.get(id.toString());
      if (name) {
        formationNamesInGroup.add(name);
      }
    }

    return hospitalFormations
      .filter((f: any) => f.name && formationNamesInGroup.has(f.name))
      .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  async getFormationsOfFormationsGroupId(formationsGroupId: string, fromHospitals: boolean) {
    const allFormations = await this.getAllFormations();
    const formationGroups = await this.getAllFormationsGroups();

    const targetGroup = formationGroups.find((group: any) => group._id?.toString() === formationsGroupId);
    if (!targetGroup || !Array.isArray(targetGroup.formations)) {
      return [];
    }

    if (!fromHospitals) {
      const formationIdSet = new Set(targetGroup.formations.map((id: any) => id.toString()));
      return allFormations
        .filter((f: any) => f._id && formationIdSet.has(f._id.toString()))
        .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    const hospitalFormations = await firstValueFrom(
      this.subscriptionManager.createRequest(
        this.paramedicalService.getFormations(),
        'getFormations',
        this.instanceId,
        true
      )
    );

    const formationIdToName = new Map<string, string>();
    for (const f of allFormations) {
      if (f._id && f.name) {
        formationIdToName.set(f._id.toString(), f.name);
      }
    }

    const formationNamesInGroup = new Set<string>();
    for (const id of targetGroup.formations) {
      const name = formationIdToName.get(id.toString());
      if (name) {
        formationNamesInGroup.add(name);
      }
    }

    return hospitalFormations
      .filter((f: any) => f.name && formationNamesInGroup.has(f.name))
      .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  async getFormationsOfFormationsGroupIds(formationGroupIds: string[], fromHospitals: boolean) {
    const allFormations = await this.getAllFormations();
    const allGroups = await this.getAllFormationsGroups();

    // Filter only the requested groups
    const relevantGroups = allGroups.filter((group: any) => formationGroupIds.includes(group._id?.toString()));

    // Collect all formation IDs from the selected groups
    const formationIdSet = new Set<string>();
    for (const group of relevantGroups) {
      if (Array.isArray(group.formations)) {
        for (const id of group.formations) {
          formationIdSet.add(id.toString());
        }
      }
    }

    if (!fromHospitals) {
      // Match by formation ID from full catalog
      const matchedFormations = allFormations.filter((f: any) => f._id && formationIdSet.has(f._id.toString()));

      // Deduplicate by _id and sort by name
      return Array.from(new Map(matchedFormations.map((f: any) => [f._id?.toString(), f])).values()).sort(
        (a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? '')
      );
    }

    // If fromHospitals is true, match by name
    const hospitalFormations = await firstValueFrom(
      this.subscriptionManager.createRequest(
        this.paramedicalService.getFormations(),
        'getFormations',
        this.instanceId,
        true
      )
    );

    // Map formation IDs to names from the full catalog
    const idToNameMap = new Map<string, string>();
    for (const f of allFormations) {
      if (f._id && f.name) {
        idToNameMap.set(f._id.toString(), f.name);
      }
    }

    // Collect all formation names from the group
    const formationNames = new Set<string>();
    for (const id of formationIdSet) {
      const name = idToNameMap.get(id);
      if (name) {
        formationNames.add(name);
      }
    }

    // Match hospital formations by name
    const matchedFormations = hospitalFormations.filter((f: any) => f.name && formationNames.has(f.name));

    // Deduplicate by name and sort
    return Array.from(new Map(matchedFormations.map((f: any) => [f.name, f])).values()).sort((a: any, b: any) =>
      (a.name ?? '').localeCompare(b.name ?? '')
    );
  }

  async getFormationsByGroupName(
    allFormations: Formation[] | undefined,
    groupName: string,
    fromHospitals: boolean = false
  ): Promise<Formation[]> {
    if (!allFormations) {
      allFormations = await this.getAllFormations(); // from AllFormations
    }

    const allGroups = await this.getAllFormationsGroups();
    const targetGroup = allGroups.find((group: any) => group.name === groupName);

    if (!targetGroup || !Array.isArray(targetGroup.formations)) {
      return [];
    }

    // Get names from the AllFormations inside the group
    const allFormationsMasterList = await this.getAllFormations(); // AllFormations
    const formationIdToName = new Map(allFormationsMasterList.map((f: any) => [f._id.toString(), f.name]));

    const formationNamesInGroup = new Set<string>();
    for (const formationId of targetGroup.formations) {
      const name = formationIdToName.get(formationId.toString());
      if (name) {
        formationNamesInGroup.add(name);
      }
    }

    if (fromHospitals) {
      const formations = await firstValueFrom(
        this.subscriptionManager.createRequest(
          this.paramedicalService.getFormations(),
          'getFormations',
          this.instanceId,
          true
        )
      );
      const filteredHospitalFormations = formations.filter((f: any) => formationNamesInGroup.has(f.name));

      return filteredHospitalFormations.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    // Filter hospital-specific formations by matching names
    const matchedFormations = allFormations.filter((f: any) => formationNamesInGroup.has(f.name ?? ''));

    return matchedFormations.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  async getFormationsByGroupId(
    allFormations: Formation[],
    groupId: string,
    fromHospitals: boolean = false
  ): Promise<Formation[]> {
    if (!allFormations) {
      allFormations = await this.getAllFormations(); // from AllFormations
    }

    const allGroups = await this.getAllFormationsGroups();
    const targetGroup = allGroups.find((group: any) => group._id === groupId);

    if (!targetGroup || !Array.isArray(targetGroup.formations)) {
      return [];
    }

    // Get names from the AllFormations inside the group
    const allFormationsMasterList = await this.getAllFormations(); // AllFormations
    const formationIdToName = new Map(allFormationsMasterList.map((f: any) => [f._id.toString(), f.name]));

    const formationNamesInGroup = new Set<string>();
    for (const formationId of targetGroup.formations) {
      const name = formationIdToName.get(formationId.toString());
      if (name) {
        formationNamesInGroup.add(name);
      }
    }

    if (fromHospitals) {
      const formations = await firstValueFrom(
        this.subscriptionManager.createRequest(
          this.paramedicalService.getFormations(),
          'getFormations',
          this.instanceId,
          true
        )
      );
      const filteredHospitalFormations = formations.filter((f: any) => formationNamesInGroup.has(f.name));

      return filteredHospitalFormations.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    // Filter hospital-specific formations by matching names
    const matchedFormations = allFormations.filter((f: any) => formationNamesInGroup.has(f.name ?? ''));

    return matchedFormations.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  async getFormationsByGroup(allFormations: Formation[], formationsGroup: FormationsGroups): Promise<Formation[]> {
    const targetGroup = formationsGroup;

    if (!targetGroup || !Array.isArray(targetGroup.formations)) {
      return [];
    }

    // Get names from the AllFormations inside the group
    const allFormationsMasterList = await this.getAllFormations(); // AllFormations
    const formationIdToName = new Map(allFormationsMasterList.map((f: any) => [f._id.toString(), f.name]));

    const formationNamesInGroup = new Set<string>();
    for (const formationId of targetGroup.formations) {
      const name = formationIdToName.get(formationId.toString());
      if (name) {
        formationNamesInGroup.add(name);
      }
    }

    // Filter hospital-specific formations by matching names
    const matchedFormations = allFormations.filter((f: any) => formationNamesInGroup.has(f.name ?? ''));

    return matchedFormations.sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  async getFormationsFromInputInsideGroup(inputList: string[], targetGroupName: string): Promise<Formation[]> {
    const allFormations = await this.getAllFormations(); // from AllFormations
    const allFormationsById = new Map(allFormations.map((f: any) => [f._id.toString(), f]));
    const allFormationsByName = new Map(allFormations.map((f: any) => [f.name, f]));

    const allFormationsGroups = await this.getAllFormationsGroups(); // list of all formationsGroups
    const targetGroup = allFormationsGroups.find((g: any) => g.name === targetGroupName);

    if (!targetGroup || !Array.isArray(targetGroup.formations)) {
      return [];
    }

    // Get the formations (by ID) inside the target group
    const groupFormationIds = new Set(targetGroup.formations.map((fId: any) => fId.toString()));

    // Step 1: resolve all formations from inputList
    const resolvedInputFormationIds = new Set<string>();

    for (const input of inputList) {
      const strInput = input.toString();

      // Match by formation _id
      if (allFormationsById.has(strInput)) {
        resolvedInputFormationIds.add(strInput);
      }

      // Match by formation name
      else if (allFormationsByName.has(strInput)) {
        const formation = allFormationsByName.get(strInput);
        if (formation) {
          resolvedInputFormationIds.add(formation._id.toString());
        }
      }

      // Match by formationsGroup _id
      else {
        const matchingGroup = allFormationsGroups.find((g: any) => g._id.toString() === strInput);
        if (matchingGroup) {
          for (const fId of matchingGroup.formations ?? []) {
            resolvedInputFormationIds.add(fId.toString());
          }
        }
      }
    }

    // Step 2: keep only the ones also in the target group
    const finalFormations = Array.from(resolvedInputFormationIds)
      .filter((id: any) => groupFormationIds.has(id))
      .map((id: any) => allFormationsById.get(id))
      .filter(Boolean) as Formation[];

    return finalFormations;
  }

  async doesAccessOptionHaveNurses(
    accessOption: AccessOptions | undefined,
    keyToSearch: string = 'positions'
  ): Promise<boolean> {
    if (!accessOption || !Array.isArray(accessOption[keyToSearch as keyof AccessOptions])) {
      return false;
    }

    const entries = accessOption[keyToSearch as keyof AccessOptions] as string[];

    if (entries.includes('NURSES')) {
      return true;
    }

    const [formations, formationsGroups] = await Promise.all([
      this.getAccessOptionsPositionsByPositions(entries),
      this.getAllFormationsGroups(),
    ]);

    const nursesGroup = formationsGroups.find((group: FormationsGroups) => group.name === 'NURSES');
    if (!nursesGroup || !nursesGroup.formations) {
      return false;
    }

    const nursesFormationIds = new Set(nursesGroup.formations.map((f: any) => f.toString()));

    return formations.some((formation: Formation) => nursesFormationIds.has(formation._id?.toString() ?? ''));
  }

  async doesAccessOptionHaveIades(
    accessOption: AccessOptions | undefined,
    keyToSearch: string = 'positions'
  ): Promise<boolean> {
    if (!accessOption || !Array.isArray(accessOption[keyToSearch as keyof AccessOptions])) {
      return false;
    }

    const entries = accessOption[keyToSearch as keyof AccessOptions] as string[];

    if (entries.includes('IADES')) {
      return true;
    }

    const [formations, formationsGroups] = await Promise.all([
      this.getAccessOptionsPositionsByPositions(entries),
      this.getAllFormationsGroups(),
    ]);

    const iadesGroup = formationsGroups.find((group: FormationsGroups) => group.name === 'IADES');
    if (!iadesGroup || !iadesGroup.formations) {
      return false;
    }

    const iadesFormationIds = new Set(iadesGroup.formations.map((f: any) => f.toString()));

    return formations.some((formation: Formation) => iadesFormationIds.has(formation._id?.toString() ?? ''));
  }

  async isProfileHybridAnesth(positions: string[]): Promise<boolean> {
    if (!positions) {
      return false;
    }

    const [formations, formationsGroups] = await Promise.all([
      this.getAccessOptionsPositionsByPositions(positions),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups.filter((group: FormationsGroups) => group.smartplanningMode === 'anesth');

    if (!anesthGroup || anesthGroup.length === 0) {
      return false;
    }

    const allFormations = anesthGroup.reduce((acc: any, group: any) => {
      return acc.concat(group.formations || []);
    }, [] as any[]);

    const anesthFormationIds = new Set(allFormations.map((f: any) => f.toString()));

    return formations.some((formation: Formation) =>
      anesthFormationIds.has(formation._id ? formation._id.toString() : '')
    );
  }

  async doesAccessOptionHaveAnesthesistes(
    accessOption: AccessOptions | undefined,
    keyToSearch: string = 'positions'
  ): Promise<boolean> {
    if (!accessOption || !Array.isArray(accessOption[keyToSearch as keyof AccessOptions])) {
      return false;
    }

    const entries = accessOption[keyToSearch as keyof AccessOptions] as string[];

    const [formations, formationsGroups] = await Promise.all([
      this.getAccessOptionsPositionsByPositions(entries),
      this.getAllFormationsGroups(),
    ]);

    const anesthesistesGroup = formationsGroups.find((group: FormationsGroups) => group.name === 'ANESTHESISTES');
    if (!anesthesistesGroup || !anesthesistesGroup.formations) {
      return false;
    }

    if (entries.includes(anesthesistesGroup._id ?? '')) {
      return true;
    }

    const anesthesistesFormationIds = new Set(anesthesistesGroup.formations.map((f: any) => f.toString()));

    return formations.some((formation: Formation) => anesthesistesFormationIds.has(formation._id?.toString() ?? ''));
  }

  async doesAccessOptionHaveSurgeons(
    accessOption: AccessOptions | undefined,
    keyToSearch: string = 'positions'
  ): Promise<boolean> {
    if (!accessOption || !Array.isArray(accessOption[keyToSearch as keyof AccessOptions])) {
      return false;
    }

    const entries = accessOption[keyToSearch as keyof AccessOptions] as string[];

    if (entries.includes('SURGEONS')) {
      return true;
    }

    const [formations, formationsGroups] = await Promise.all([
      this.getAccessOptionsPositionsByPositions(entries),
      this.getAllFormationsGroups(),
    ]);

    const surgeonsGroup = formationsGroups.find((group: FormationsGroups) => group.name === 'SURGEONS');
    if (!surgeonsGroup || !surgeonsGroup.formations) {
      return false;
    }

    const surgeonsFormationIds = new Set(surgeonsGroup.formations.map((f: any) => f.toString()));

    return formations.some((formation: Formation) => surgeonsFormationIds.has(formation._id?.toString() ?? ''));
  }

  async doesAccessOptionHaveCDB(
    accessOption: AccessOptions | undefined,
    keyToSearch: string = 'positions'
  ): Promise<boolean> {
    if (!accessOption || !Array.isArray(accessOption[keyToSearch as keyof AccessOptions])) {
      return false;
    }

    const entries = accessOption[keyToSearch as keyof AccessOptions] as string[];

    if (entries.includes('CDB')) {
      return true;
    }

    const [formations, formationsGroups] = await Promise.all([
      this.getAccessOptionsPositionsByPositions(entries),
      this.getAllFormationsGroups(),
    ]);

    const cdbGroup = formationsGroups.find((group: FormationsGroups) => group.name === 'CDB');
    if (!cdbGroup || !cdbGroup.formations) {
      return false;
    }

    const cdbFormationIds = new Set(cdbGroup.formations.map((f: any) => f.toString()));

    return formations.some((formation: Formation) => cdbFormationIds.has(formation._id?.toString() ?? ''));
  }

  async getPositionAllFormationObject(position: string): Promise<AllFormation | undefined> {
    const allFormations = await this.getAllFormations();

    return allFormations.find((allFormation: AllFormation) => allFormation.name === position);
  }

  async isUserInAnesthesistes(): Promise<boolean> {
    const userPosition = this.getCurrentUser().profile?.position ?? '';
    if (!userPosition) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(userPosition),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'ANESTHESISTES');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isUserInIades(): Promise<boolean> {
    const userPosition = this.getCurrentUser().profile?.position ?? '';
    if (!userPosition) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(userPosition),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'IADES');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isUserInNurses(): Promise<boolean> {
    const userPosition = this.getCurrentUser().profile?.position ?? '';
    if (!userPosition) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(userPosition),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'NURSES');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isUserInSurgeons(): Promise<boolean> {
    const userPosition = this.getCurrentUser().profile?.position ?? '';
    if (!userPosition) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(userPosition),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'SURGEONS');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isUserInCDB(): Promise<boolean> {
    const userPosition = this.getCurrentUser().profile?.position ?? '';
    if (!userPosition) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(userPosition),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'CDB');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isProfileInAnesthesistes(profile: Profile | undefined): Promise<boolean> {
    if (!profile?.position) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(profile.position),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'ANESTHESISTES');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isProfileInIades(profile: Profile | undefined): Promise<boolean> {
    if (!profile?.position) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(profile.position),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'IADES');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isProfileInNurses(profile: Profile | undefined): Promise<boolean> {
    if (!profile?.position) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(profile.position),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'NURSES');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isProfileInSurgeons(profile: Profile | undefined): Promise<boolean> {
    if (!profile?.position) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(profile.position),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'SURGEONS');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async isProfileInCDB(profile: Profile | undefined): Promise<boolean> {
    if (!profile?.position) {
      return false;
    }

    const [formation, formationsGroups] = await Promise.all([
      this.getPositionAllFormationObject(profile.position),
      this.getAllFormationsGroups(),
    ]);

    const anesthGroup = formationsGroups
      .filter((formationsGroup: FormationsGroups) =>
        (this.usersHospitals?.[0].hospitalType as HospitalTypes).formationsGroups?.includes(formationsGroup._id ?? '')
      )
      .find((group: FormationsGroups) => group.name === 'CDB');

    if (!anesthGroup || !anesthGroup.formations) {
      return false;
    }

    return (anesthGroup.formations as string[]).some(
      (allFormationsId: string) => String(allFormationsId) === String(formation?._id ?? '')
    );
  }

  async getFormationsGroupOfProfile(profile: Profile | undefined): Promise<FormationsGroups | undefined> {
    if (!profile?.position) {
      return undefined;
    }

    const [allFormations, formationsGroups] = await Promise.all([
      this.getAllFormations(),
      this.getAllFormationsGroups(),
    ]);

    const DEFAULT_GROUPS = new Set(['NURSES', 'ANESTHESISTES', 'IADES', 'SURGEONS', 'CDB']);

    let fallbackGroup: FormationsGroups | undefined = undefined;

    for (const group of formationsGroups) {
      if (!Array.isArray(group.formations)) {
        continue;
      }

      const groupFormations = allFormations.filter((formation: any) =>
        group.formations?.some((f: any) => f.toString() === formation._id?.toString())
      );

      if (groupFormations.some((f: any) => f.name === profile.position)) {
        if (group.name && !DEFAULT_GROUPS.has(group.name)) {
          return group; // ✅ Found a preferred (non-default) group
        }
        fallbackGroup ??= group; // Store first matching default group as fallback
      }
    }

    return fallbackGroup;
  }

  async getFormationsGroupsOfAccessOption(availablePositions: string[]) {
    const DEFAULT_GROUPS = ['NURSES', 'ANESTHESISTES', 'IADES', 'SURGEONS', 'CDB'];

    const [allFormationsGroups, allFormations] = await Promise.all([
      this.getAllFormationsGroups(),
      this.getAllFormations(),
    ]);

    const hospitalType = this.usersHospitals?.[0].hospitalType as HospitalTypes;

    const allowedGroups = allFormationsGroups.filter((group: any) =>
      hospitalType?.formationsGroups?.includes(group._id?.toString() ?? '')
    );

    const formationById = new Map<string, any>();
    const formationByName = new Map<string, any>();
    allFormations.forEach((f: any) => {
      if (f._id) {
        formationById.set(f._id.toString(), f);
      }
      if (f.name) {
        formationByName.set(f.name, f);
      }
    });

    const matchedFormationsToGroup = new Map<string, any>();

    for (const group of allowedGroups) {
      const groupId = group._id?.toString();
      const groupIsMatched = availablePositions.includes(groupId ?? '');

      const formationIds = group.formations?.map((id: any) => id.toString()) ?? [];

      for (const fid of formationIds) {
        const formation = formationById.get(fid);
        if (!formation) {
          continue;
        }

        const formationMatches = availablePositions.includes(fid) || availablePositions.includes(formation.name);

        if (!formationMatches && !groupIsMatched) {
          continue;
        }

        const existingGroup = matchedFormationsToGroup.get(fid);
        const isExistingDefault = DEFAULT_GROUPS.includes(existingGroup?.name ?? '');
        const isCurrentDefault = DEFAULT_GROUPS.includes(group.name ?? '');

        if (!existingGroup || (isExistingDefault && !isCurrentDefault)) {
          matchedFormationsToGroup.set(fid, group);
        }
      }
    }

    const uniqueGroupsMap = new Map<string, any>();
    for (const group of matchedFormationsToGroup.values()) {
      if (group?._id) {
        uniqueGroupsMap.set(group._id.toString(), group);
      }
    }

    return Array.from(uniqueGroupsMap.values());
  }

  removeOverlappingFormationsFromDefaults(formationsGroups: FormationsGroups[]): FormationsGroups[] {
    const DEFAULT_GROUPS = ['NURSES', 'ANESTHESISTES', 'IADES', 'SURGEONS', 'CDB'];

    const nonDefaultGroups = formationsGroups.filter(
      (group: FormationsGroups) => !DEFAULT_GROUPS.includes(group.name ?? '')
    );

    const nonDefaultFormationIds = new Set<string>();
    for (const group of nonDefaultGroups) {
      for (const fid of group.formations ?? []) {
        nonDefaultFormationIds.add(fid.toString());
      }
    }

    const result: any[] = [];

    for (const group of formationsGroups) {
      if (DEFAULT_GROUPS.includes(group.name ?? '')) {
        const originalFormations = group.formations ?? [];
        const updatedFormations = originalFormations.filter((fid: any) => !nonDefaultFormationIds.has(fid.toString()));

        result.push({
          ...group,
          formations: updatedFormations,
        });
      } else {
        result.push(group);
      }
    }

    return result;
  }

  getHospitalNames(): HospitalNames {
    const hospitals = this.usersHospitals;
    const hospitalType: HospitalTypes = (
      hospitals?.find((hospital: Hospital) => hospital._id === this.getSelectedHospitals()[0]) ?? hospitals?.[0]
    )?.hospitalType as HospitalTypes;
    const result: HospitalNames = {
      roomsName: hospitalType?.nomenclature?.roomsName ?? 'Salle',
      recoveryRoomsName: hospitalType?.nomenclature?.recoveryRoomsName ?? 'Cocon',
      blocName: hospitalType?.nomenclature?.blocName ?? 'Bloc',
      consultationName: hospitalType?.nomenclature?.consultationName ?? 'Consultation',
      extracliniqueName: hospitalType?.nomenclature?.extracliniqueName ?? 'Extraclinique',
      showRoomNameOnRooms: hospitalType?.nomenclature?.showRoomNameOnRooms ?? true,
      roomsNameGenreMasculin: hospitalType?.nomenclature?.roomsNameGenreMasculin ?? false,
      recoveryRoomsNameGenreMasculin: hospitalType?.nomenclature?.recoveryRoomsNameGenreMasculin ?? true,
      vacationsName: hospitalType?.nomenclature?.vacationsName ?? 'Mission',
      surgeonOpeningsName: hospitalType?.nomenclature?.surgeonOpeningsName ?? 'Vacation',
      vacatairesName: hospitalType?.nomenclature?.vacatairesName ?? 'Vacataire',
      specialtiesName: hospitalType?.nomenclature?.specialtiesName ?? 'Spécialité',
      vacationsNameGenreMasculin: hospitalType?.nomenclature?.vacationsNameGenreMasculin ?? false,
      specialtiesNameGenreMasculin: hospitalType?.nomenclature?.specialtiesNameGenreMasculin ?? false,
      surgeonsName: hospitalType?.nomenclature?.surgeonsName ?? 'Chirurgien',
      surgeonsNameGenreMasculin: hospitalType?.nomenclature?.surgeonsNameGenreMasculin ?? true,
      hospitalsName: hospitalType?.nomenclature?.hospitalsName ?? 'Hôpital',
      hospitalsNameGenreMasculin: hospitalType?.nomenclature?.hospitalsNameGenreMasculin ?? true,
      paramedicalsName: hospitalType?.nomenclature?.paramedicalsName ?? 'Paramédical',
      paramedicalsNameGenreMasculin: hospitalType?.nomenclature?.paramedicalsNameGenreMasculin ?? true,
      cyclesName: hospitalType?.nomenclature?.cyclesName ?? 'Roulement',
      cyclesNameGenreMasculin: hospitalType?.nomenclature?.cyclesNameGenreMasculin ?? true,
      tvosName: hospitalType?.nomenclature?.tvosName ?? 'TVO',
      tvosNameGenreMasculin: hospitalType?.nomenclature?.tvosNameGenreMasculin ?? true,
      surgeonOpeningsNameGenreMasculin: hospitalType?.nomenclature?.surgeonOpeningsNameGenreMasculin ?? false,
      interventionsName: hospitalType?.nomenclature?.interventionsName ?? 'Intervention',
      interventionsNameGenreMasculin: hospitalType?.nomenclature?.interventionsNameGenreMasculin ?? false,
      practitionersName: hospitalType?.nomenclature?.practitionersName ?? 'Praticien',
      practitionersNameGenreMasculin: hospitalType?.nomenclature?.practitionersNameGenreMasculin ?? true,
    };

    return result;
  }

  // async getProfilesListeAccessOptionByProfilePosition(
  //   position: string | undefined
  // ): Promise<AccessOptions | undefined> {
  //   const currentUser = this.storageService.getUser();
  //   const profilesListesAccessOptions = ((currentUser.persona as Personas)?.accessOptions as AccessOptions[])?.filter(
  //     (accessOption: AccessOptions) => accessOption.pageUrl?.includes('/hrsuite/liste')
  //   );

  //   for (const accessOption of profilesListesAccessOptions) {
  //     const accessOptionPositions = await this.getAccessOptionsPositionsByPositions(accessOption.positions);

  //     if (accessOptionPositions.some((formation: Formation) => formation.name === position)) {
  //       return accessOption;
  //     }
  //   }

  //   return undefined;
  // }

  isBasicService(): boolean {
    const hospitals = this.usersHospitals;
    const hospitalType: HospitalTypes = (
      hospitals?.find((hospital: Hospital) => hospital._id === this.getSelectedHospitals()[0]) ?? hospitals?.[0]
    )?.hospitalType as HospitalTypes;

    if (hospitalType) {
      return hospitalType.showEffectiveNeedsByReasonsMaps ?? false;
    }

    return false;
  }

  clear() {
    this.changedHospitalsSelectedSubject.complete();
    this.changedHospitalsSelectedSubject = new Subject<void>();

    this.soloHospitalModeSubject.complete();
    this.soloHospitalModeSubject = new Subject<boolean>();

    this.sideNav.complete();
    this.sideNav = new Subject<void>();

    this.jobDescriptionToggle.complete();
    this.jobDescriptionToggle = new Subject<boolean>();

    this.rebuildMenuSubject.complete();
    this.rebuildMenuSubject = new Subject<void>();

    this.allFormations = undefined;
    this.allFormationsGroups = undefined;
    this.allFormationsPromise = null;
    this.allFormationsGroupsPromise = null;
  }
}
